import {
  Children,
  isValidElement,
  type ComponentPropsWithoutRef,
  type ReactNode
} from 'react';
import styles from './tile.module.scss';
import { TileActions, TileMenu } from './TileActions';

/** Status variant determines the color scheme for the status badge */
export type TileStatusVariant = 'green' | 'blue' | 'grey' | 'red';

/** Item type affects styling and layout */
export type TileItemType =
  | 'listing'
  | 'waitlist'
  | 'litter'
  | 'breedingDog'
  | 'provider'
  | 'article'
  | 'pup';

export type TileProps = {
  /** Item type for semantic/styling purposes */
  itemType?: TileItemType;
  /** Render function for link wrapper (for framework-specific Link component) */
  renderLink?: (props: {
    children: ReactNode;
    className?: string;
    'aria-label'?: string;
  }) => ReactNode;
  /** Click handler (used when no renderLink provided) */
  onClick?: () => void;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Whether this tile is in draft/inactive state */
  isDraft?: boolean;
  /** Whether to use horizontal layout (always horizontal on mobile) */
  horizontal?: boolean;
  /** Custom class for the tile */
  className?: string;
  /** Children to render inside the tile */
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<'article'>, 'onClick' | 'children'>;

/**
 * Separate TileActions from other children so actions can be rendered outside the link.
 * This prevents nested anchor tags and ensures buttons work properly.
 */
function separateActions(children: ReactNode): {
  linkContent: ReactNode[];
  actionsContent: ReactNode[];
} {
  const linkContent: ReactNode[] = [];
  const actionsContent: ReactNode[] = [];

  Children.forEach(children, (child) => {
    if (
      isValidElement(child) &&
      (child.type === TileActions || child.type === TileMenu)
    ) {
      actionsContent.push(child);
    } else {
      linkContent.push(child);
    }
  });

  return { linkContent, actionsContent };
}

/**
 * Tile - A flexible, composable tile component for displaying items.
 *
 * This is a low-level component that provides the common tile structure.
 * Use composition with sub-components for specific content:
 *
 * @example
 * ```tsx
 * <Tile itemType="listing" renderLink={(props) => <Link href="/listing/123" {...props} />}>
 *   <TileImage imageUrl="/photo.jpg" />
 *   <TileContent>
 *     <TileTitle>Puppy Name</TileTitle>
 *     <TilePrice>$2,500</TilePrice>
 *     <TileDetails breed="Golden Retriever" location="Toronto, ON" />
 *   </TileContent>
 *   <TileStatus status="Available" variant="green" />
 *   <TileActions>
 *     <Button>View Transactions</Button>
 *   </TileActions>
 * </Tile>
 * ```
 *
 * Note: TileActions is rendered OUTSIDE the link wrapper to prevent nested anchor tags.
 */
export function Tile({
  itemType,
  renderLink,
  onClick,
  ariaLabel,
  isDraft = false,
  horizontal = false,
  className,
  children,
  ...rest
}: TileProps) {
  const combinedClassName = [
    styles.tile,
    isDraft && styles.tileDraft,
    horizontal && styles.tileHorizontal,
    className
  ]
    .filter(Boolean)
    .join(' ');

  if (renderLink) {
    // Separate TileActions from other children to render outside the link
    const { linkContent, actionsContent } = separateActions(children);

    return (
      <article
        className={combinedClassName}
        data-item-type={itemType}
        {...rest}
      >
        {renderLink({
          children: linkContent,
          className: styles.tileLink,
          'aria-label': ariaLabel
        })}
        {actionsContent}
      </article>
    );
  }

  return (
    <article
      className={combinedClassName}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      aria-label={onClick ? undefined : ariaLabel}
      data-item-type={itemType}
      {...rest}
    >
      {children}
    </article>
  );
}
